/**
 * KnockCacheInvalidator
 *
 * Listens to real-time Knock feed events and invalidates React Query
 * caches so that polling-dependent pages refresh immediately when
 * background work completes (question generation, report generation).
 *
 * Must be mounted inside both KnockFeedProvider and QueryClientProvider.
 */

import { useEffect } from "react";
import { useKnockFeed } from "@knocklabs/react";
import { useQueryClient } from "@tanstack/react-query";

/** Knock workflow slugs that trigger cache invalidation. */
const INVALIDATION_MAP: Record<string, string[]> = {
  "question-generation-complete": ["questions"],
  "report-generated": ["assessment"],
};

/**
 * Subscribes to Knock real-time feed events and invalidates the
 * corresponding React Query cache when a relevant notification arrives.
 *
 * @returns null — renders nothing, side-effect only
 */
export function KnockCacheInvalidator(): null {
  const { feedClient } = useKnockFeed();
  const queryClient = useQueryClient();

  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const handler = ({ items }: { items: any[] }) => {
      for (const item of items) {
        const source = item.source?.key as string | undefined;
        const assessmentId = item.data?.assessmentId as string | undefined;
        if (!source || !assessmentId) continue;

        const queryKeys = INVALIDATION_MAP[source];
        if (queryKeys) {
          for (const key of queryKeys) {
            queryClient.invalidateQueries({ queryKey: [key] });
          }
        }
      }
    };

    feedClient.on("items.received.realtime", handler);
    return () => feedClient.off("items.received.realtime", handler);
  }, [feedClient, queryClient]);

  return null;
}
